import {
  Required,
  Schema,
  TextField,
  RemoteDataSource,
  ForeignKeyField,
  DynamicColumn
} from "lego-gui-lib/lib";
import { mainStore, api } from "../../core/mainStore";
import { CustomerFK } from "../customer/schema";
import get from "lodash/get";
import {
  customerChanged,
  customerInit,
  onVisibilityChange,
  onVisibilityInit,
  VisibilityEnum,
  VisibilityType,
  withVisibility
} from "../../common/common";
import { t } from "../../core/i18n/i18n";
import { CustomerMessagesFK, MessageFk } from "../message/schema";
import { Auth } from "../../core/model/auth";
import { createContext } from "react";
import { ContextAwareListDataSource } from "../../common/ContextAwareListDataSource";
import { AccessContext } from "lego-gui-lib";

const auth = mainStore.auth;

export const LanguageSelectionSchema = new Schema<DynamicColumn>({
  fields: [
    {
      name: "name",
      title: t("common:name"),
      type: TextField(Required)
    },
    {
      name: "visibility",
      title: t("common:visibility"),
      type: VisibilityEnum(),
      readonly: () => !auth.isAdmin(),
      onChange: (newValue, oldValue, formState) => {
        onVisibilityChange(newValue, oldValue, formState);
        formState.values["msgLanguageSelectRef"] = undefined;
      },
      onInit: onVisibilityInit
    },
    {
      name: "customerRef",
      title: t("hns:customer:domain"),
      type: CustomerFK,
      if: () => auth.isAdmin(),
      onChange: customerChanged,
      onInit: customerInit
    },
    {
      name: "msgLanguageSelectRef",
      title: t("hns:languageSelection:message"),
      type: () => (auth.isAnyCustomerOwner() ? CustomerMessagesFK : MessageFk)
    }
  ],
  primaryKey: ["id"],
  defaultSort: { sort: "visibility", order: "desc" },
  security: {
    readableRole: Auth.Roles.ANYONE,
    editableRole: Auth.Roles.ANYONE_WITH_WRITE
  }
});

export const LanguageSelectionSchemaForForms = LanguageSelectionSchema.extend({
  visibility: {
    type: VisibilityEnum(),
    onChange: (newValue, oldValue, formState) => {
      onVisibilityChange(newValue, oldValue, formState);
      if (newValue === VisibilityType.PUBLIC) {
        formState.values["msgLanguageSelectRef"] = null;
      }
    }
  },
  customerRef: {
    if: (ctx: AccessContext) =>
      auth.isAdmin() && get(ctx, "data.visibility") === VisibilityType.PRIVATE
  },
  msgLanguageSelectRef: {
    type: CustomerMessagesFK
  }
});

export const EditLanguageSelectionSchema = LanguageSelectionSchemaForForms.extend(
  {
    visibility: { readonly: true },
    customerRef: { readonly: true }
  }
);

mainStore.languageSelection = RemoteDataSource.create({
  api: api,
  name: "languageSelection",
  schema: LanguageSelectionSchema,
  url: "/languageSelections",
  commons: mainStore.commons
});

export const languageSelectionContext = createContext(
  mainStore.languageSelection
);

mainStore.customerLanguageSelection = new ContextAwareListDataSource({
  api: api,
  name: "languageSelection",
  schema: LanguageSelectionSchema,
  url: "/languageSelections",
  commons: mainStore.commons,
  observableFieldRef: "customerRef",
  observableDataSource: "customer"
});

export const customerLanguageSelectionContext = createContext(
  mainStore.customerLanguageSelection
);

mainStore.languageSelectionContexAwareFK = RemoteDataSource.create({
  api: api,
  name: "languageSelection",
  schema: LanguageSelectionSchema,
  url: "/languageSelections",
  commons: mainStore.commons
});

export const LanguageSelectionContexAwareFK = ForeignKeyField({
  dataSource: mainStore.languageSelectionContexAwareFK.listDataSource,
  searchField: "name",
  labelField: withVisibility(),
  valueField: "id",
  minWidth: 70
});

export const LanguageSelectionFKNotRequired = ForeignKeyField({
  dataSource: mainStore.languageSelection.listDataSource,
  searchField: "name",
  labelField: withVisibility(),
  valueField: "id",
  minWidth: 70
});
